import qs from 'qs'

import { Account } from '@modules-deprecated/app/accounts/types'

import { Origin } from '../../enums/origin'
import { PeriodMode } from '../../enums/periodMode'
import { SortDirection } from '../../enums/sortDirection'
import { FileWithOrigin } from '../../types/fileWithOrigin'
import { Metable } from '../../types/metable'
import { UploadFileHeaders } from '../../types/uploadFilesHeaders'
import { getCurrentLocale, getRequest, postFileRequest, postV2Request, putRequest } from '../../utils'
import { AccountStatement } from './types/accountStatement'
import { BankStatementFile } from './types/bankStatementFile'

export interface FetchAccountStatementsPayload {
  accountId: string
  includeVoided?: boolean
  pageSize?: number
  period?: string
  q?: string
  sortDirection?: SortDirection
}

export interface FetchAccountStatementsResponse extends Metable {
  accountStatement: AccountStatement
}

export interface SaveAccountPayload {
  accountNo: number | null
  bankAccountNo?: string | null
  bankIban?: string
  bankId?: string
  bankName?: string
  bankRoutingNo?: number | null
  bankSwift?: string
  currencyId?: string
  description?: string
  groupId?: string
  id?: string
  isArchived?: boolean
  isBankAccount?: boolean
  isPaymentEnabled?: boolean
  name: string
  natureId?: string
  organizationId?: string
  publicAccountId?: string | null
  taxRateId?: string
}

export interface NewBankAccountPayload {
  name: string
  description: string
  currencyId: string
}

export interface CreateNewBookkeepingBankAccountProps {
  organizationId: string
  newBankAccountPayload: NewBankAccountPayload
}

export interface CreateNewBookkeepingBankAccountResponse extends Metable {
  account: Account
  accounts: Account[]
}

export interface UpdateAccountResponse extends Metable {
  accounts: Account[]
}

export interface SetAccountArchivedStatusPayload {
  accountId: string
  isArchived: boolean
}

export const setAccountArchivedStatus = ({ accountId, isArchived }: SetAccountArchivedStatusPayload) =>
  putRequest(`/v2/accounts/${accountId}`, {
    account: {
      id: accountId,
      isArchived,
    },
  })

export const fetchAccountStatements = ({
  accountId,
  includeVoided,
  pageSize,
  period,
  q,
  sortDirection,
}: FetchAccountStatementsPayload): Promise<FetchAccountStatementsResponse> => {
  const queryParams = qs.stringify({
    limit: pageSize || 1000,
    ...(includeVoided !== undefined ? { includeVoided } : {}),
    ...(period && period !== PeriodMode.All ? { period } : {}),
    ...(q ? { q } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  })

  const locale = getCurrentLocale()

  return getRequest(`/v2/accounts/${accountId}/statement?${queryParams}`, { 'accept-language': locale })
}

export const createNewBookkeepingBankAccount = ({
  organizationId,
  newBankAccountPayload,
}: CreateNewBookkeepingBankAccountProps): Promise<CreateNewBookkeepingBankAccountResponse> => {
  return postV2Request({
    endpoint: `/v2/bankAccounts?organizationId=${organizationId}`,
    payload: {
      record: newBankAccountPayload,
    },
  })
}

export const updateAccount = (accountData: Partial<SaveAccountPayload>): Promise<UpdateAccountResponse> =>
  putRequest(`/v2/accounts/${accountData.id}`, { account: accountData })

export interface UploadBankStatementResponseData extends Metable {
  data: BankStatementFile
}

export const uploadBankStatementFile = (
  file: File,
  apiHeaders?: UploadFileHeaders,
): Promise<UploadBankStatementResponseData> => {
  const updatedFile: FileWithOrigin = file
  updatedFile.origin = Origin.Web

  return postFileRequest('/v2/bankStatements/parse', updatedFile, apiHeaders ? { headers: apiHeaders } : {})
}
